"use client";
import { GlassButton } from "../button/GlassButton";
import { GlassTooltip } from "../modal/GlassPopover";

import { cn } from "../../lib/utilsComprehensive";
import { ChevronLeft, ChevronRight } from "@/icons";
import { ANIMATION } from "../../tokens/designConstants";
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { OptimizedGlass } from "../../primitives";
import { Motion } from "../../primitives";

export interface NavigationItem {
  id: string;
  label: string;
  href?: string;
  icon?: React.ReactNode;
  badge?: string | number;
  children?: NavigationItem[];
  disabled?: boolean;
  onClick?: () => void;
}

export interface GlassSidebarProps {
  /**
   * Navigation items
   */
  items: NavigationItem[];
  /**
   * Currently active item
   */
  activeId?: string;
  /**
   * Sidebar variant
   */
  variant?: "default" | "compact" | "floating" | "overlay";
  /**
   * Sidebar width
   */
  width?: "sm" | "md" | "lg" | "xl";
  /**
   * Whether sidebar is collapsible
   */
  collapsible?: boolean;
  /**
   * Whether sidebar is collapsed
   */
  collapsed?: boolean;
  /**
   * Callback when collapsed state changes
   */
  onCollapsedChange?: (collapsed: boolean) => void;
  /**
   * Whether sidebar is open (for mobile/overlay)
   */
  open?: boolean;
  /**
   * Render inside the parent container instead of using viewport-fixed overlay behavior.
   */
  contained?: boolean;
  /**
   * Whether to render the collapse toggle.
   */
  showToggle?: boolean;
  /**
   * Portal the collapse toggle to document.body. Defaults to true for viewport sidebars and false in contained mode.
   */
  renderToggleInPortal?: boolean;
  /**
   * Bounded sidebar height for contained/card previews.
   */
  height?: React.CSSProperties["height"];
  /**
   * Bounded sidebar max-height for contained/card previews.
   */
  maxHeight?: React.CSSProperties["maxHeight"];
  /**
   * Inline style override for the sidebar surface.
   */
  style?: React.CSSProperties;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Sidebar header content
   */
  header?: React.ReactNode;
  /**
   * Sidebar footer content
   */
  footer?: React.ReactNode;
  /**
   * Custom logo/brand
   */
  logo?: React.ReactNode;
  /**
   * Custom user info
   */
  userInfo?: React.ReactNode;
  /**
   * Navigation callback
   */
  onNavigate?: (item: NavigationItem) => void;
  /**
   * Custom item renderer
   */
  renderItem?: (item: NavigationItem, level: number) => React.ReactNode;
  className?: string;
  "aria-label"?: string;
}

interface SidebarContextValue {
  collapsed: boolean;
  activeId?: string;
  onNavigate?: (item: NavigationItem) => void;
  renderItem?: (item: NavigationItem, level: number) => React.ReactNode;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined
);

const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("Sidebar components must be used within GlassSidebar");
  }
  return context;
};

/**
 * GlassSidebar component
 * A glassmorphism sidebar navigation with advanced features
 */
export const GlassSidebar = forwardRef<HTMLDivElement, GlassSidebarProps>(
  (
    {
      items,
      activeId,
      variant = "default",
      width = "md",
      collapsible = true,
      collapsed = false,
      onCollapsedChange,
      open = true,
      contained = false,
      showToggle = true,
      renderToggleInPortal,
      height,
      maxHeight,
      onOpenChange,
      header,
      footer,
      logo,
      userInfo,
      onNavigate,
      renderItem,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [edgeX, setEdgeX] = useState<number | null>(null);
    const [internalCollapsed, setInternalCollapsed] = useState(collapsed);
    const [mounted, setMounted] = useState(false);
    const isCollapsed = collapsed ?? internalCollapsed;
    const useViewportOverlay = variant === "overlay" && !contained;
    const shouldPortalToggle = renderToggleInPortal ?? !contained;

    const widthClasses = {
      sm: isCollapsed ? "w-16" : "w-48",
      md: isCollapsed ? "w-16" : "w-64",
      lg: isCollapsed ? "w-20" : "w-72",
      xl: isCollapsed ? "w-20" : "w-80",
    };

    const variantClasses = {
      default: "border-r border-border/20",
      compact: "border-r border-border/10",
      floating: "glass-mx-4 glass-my-4 squiricle border border-border/20",
      overlay: "border-r border-border/20 shadow-xl",
    };

    const handleToggleCollapse = () => {
      const newCollapsed = !isCollapsed;
      setInternalCollapsed(newCollapsed);
      onCollapsedChange?.(newCollapsed);
    };

    const handleItemClick = (item: NavigationItem) => {
      if (item?.disabled) return;
      onNavigate?.(item);
      if (useViewportOverlay) {
        onOpenChange?.(false);
      }
    };

    const contextValue: SidebarContextValue = {
      collapsed: isCollapsed,
      activeId,
      onNavigate: handleItemClick,
      renderItem,
    };

    if (variant === "overlay" && !open) {
      return null;
    }

    // Map width to px for fixed toggle placement
    const widthPxMap: Record<"sm" | "md" | "lg" | "xl", number> = {
      sm: 192,
      md: 256,
      lg: 288,
      xl: 320,
    };
    const collapsedWidthPxMap: Record<"sm" | "md" | "lg" | "xl", number> = {
      sm: 64,
      md: 64,
      lg: 80,
      xl: 80,
    };

    // Measure actual sidebar right edge in viewport coordinates
    useLayoutEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const update = () => {
        const rect = el.getBoundingClientRect();
        setEdgeX(rect.left + rect.width);
      };
      update();
      let ro: ResizeObserver | null = null;
      if (typeof window !== "undefined" && "ResizeObserver" in window) {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      }
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      return () => {
        if (ro) ro.disconnect();
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    }, [isCollapsed]);

    useEffect(() => setMounted(true), []);

    const fallbackX = (isCollapsed ? collapsedWidthPxMap : widthPxMap)[width];
    const centerX = edgeX ?? fallbackX;
    const toggleButton = (
      <button
        type="button"
        onClick={handleToggleCollapse}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          shouldPortalToggle
            ? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            : "absolute glass-right-2 glass-top-3",
          "w-10 h-10 glass-radius-full flex items-center justify-center",
          "glass-backdrop-blur-lg border",
          "glass-focus glass-touch-target glass-contrast-guard",
          "shadow-[0_6px_18px_color-mix(in_srgb,var(--glass-black)_35%,transparent),inset_0_1px_2px_var(--glass-bg-active)] ring-1",
          "hover:bg-white/15 active:bg-white/20"
        )}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight
            className="glass-w-5 glass-h-5 glass-text-primary glass-drop-shadow-custom"
            strokeWidth={3}
          />
        ) : (
          <ChevronLeft
            className="glass-w-5 glass-h-5 glass-text-primary glass-drop-shadow-custom"
            strokeWidth={3}
          />
        )}
      </button>
    );

    return (
      <SidebarContext.Provider data-glass-component value={contextValue}>
        <Motion
          preset={useViewportOverlay ? "slideRight" : "none"}
          className={cn(
            useViewportOverlay && "fixed inset-y-0 left-0 z-50 h-screen",
            contained && "glass-contained",
            "relative overflow-visible z-[60]"
          )}
          style={{ overflow: "visible" }}
        >
          <OptimizedGlass
            ref={(node) => {
              containerRef.current = node as HTMLDivElement | null;
              if (typeof ref === "function") ref(node as any);
              else if (ref && typeof (ref as any) === "object")
                (ref as any).current = node;
            }}
            intent="primary"
            elevation={variant === "floating" ? "level2" : "level1"}
            intensity="medium"
            depth={variant === "floating" ? 3 : 2}
            tint="lavender"
            border={variant === "floating" ? "gradient" : "subtle"}
            animation="none"
            performanceMode="medium"
            role="navigation"
            aria-label={props["aria-label"] || "Main navigation"}
            className={cn(
              "h-full flex flex-col transition-colors overflow-visible",
              contained ? "min-h-0" : "min-h-[420px] max-h-[calc(100vh-2rem)]",
              useViewportOverlay && "h-screen max-h-screen",
              contained && "glass-contained",
              widthClasses?.[width],
              variantClasses?.[variant],
              variant === "floating" ? "squiricle" : "",
              className
            )}
            style={{
              ...style,
              overflow: "visible",
              height: height ?? style?.height,
              maxHeight: maxHeight ?? style?.maxHeight,
            }}
            {...props}
          >
            {/* Background tints removed to respect global layout color */}
            {/* Header */}
            {(header || logo) && (
              <div className="glass-flex-shrink-0 glass-p-4 glass-border-b glass-border-glass-border/20">
                {header || (
                  <div className="glass-flex glass-items-center glass-justify-center glass-w-full">
                    {logo && (
                      <div
                        className={cn(
                          `flex items-center glass-gap-3 transition-all duration-[${ANIMATION.DURATION.normal}ms]`,
                          isCollapsed
                            ? "scale-75 opacity-70"
                            : "scale-100 opacity-100"
                        )}
                      >
                        {logo}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* User Info */}
            {userInfo && !isCollapsed && (
              <div className="glass-flex-shrink-0 glass-p-4 glass-border-b glass-border-glass-border/10">
                {userInfo}
              </div>
            )}

            {/* Navigation */}
            <div
              className={cn(
                "flex-1 glass-px-4 glass-py-2",
                isCollapsed
                  ? "overflow-hidden"
                  : "overflow-y-auto overflow-x-visible"
              )}
            >
              <SidebarNavigation items={items} />
            </div>

            {/* Footer */}
            {footer && (
              <div className="glass-flex-shrink-0 glass-p-4 glass-border-t glass-border-glass-border/20">
                {footer}
              </div>
            )}

            {showToggle &&
              collapsible &&
              mounted &&
              (!shouldPortalToggle || contained) &&
              toggleButton}
          </OptimizedGlass>
        </Motion>

        {showToggle &&
          collapsible &&
          mounted &&
          shouldPortalToggle &&
          !contained &&
          createPortal(
            <div
              className="glass-fixed glass-top-0 glass-h-screen glass-z-max"
              style={{
                left: Math.max(0, centerX - 24),
                width: 48,
                pointerEvents: "auto",
              }}
            >
              {toggleButton}
            </div>,
            document.body
          )}

        {/* Overlay backdrop */}
        {useViewportOverlay && open && (
          <div
            className="glass-fixed glass-inset-0 glass-surface-dark/50 glass-backdrop-blur-sm glass-z-40 glass-contrast-guard"
            onClick={(e) => onOpenChange?.(false)}
          />
        )}
      </SidebarContext.Provider>
    );
  }
);

GlassSidebar.displayName = "GlassSidebar";

/**
 * SidebarNavigation component
 */
interface SidebarNavigationProps {
  items: NavigationItem[];
  level?: number;
}

function SidebarNavigation({ items, level = 0 }: SidebarNavigationProps) {
  return (
    <ul className={cn("glass-gap-2", level > 0 && "glass-ml-4")}>
      {(items || []).map((item) => (
        <SidebarNavigationItem key={item?.id} item={item} level={level} />
      ))}
    </ul>
  );
}

/**
 * SidebarNavigationItem component
 */
interface SidebarNavigationItemProps {
  item: NavigationItem;
  level: number;
}

function SidebarNavigationItem({ item, level }: SidebarNavigationItemProps) {
  const { collapsed, activeId, onNavigate, renderItem } = useSidebarContext();
  // Only expand the most essential group by default
  const shouldExpandByDefault = level === 0 && item?.id === "home";
  const [isExpanded, setIsExpanded] = useState(shouldExpandByDefault);

  const isActive = activeId === item?.id;
  const hasChildren = item?.children && item?.children.length > 0;

  if (renderItem) {
    return <li>{renderItem(item, level)}</li>;
  }

  const handleClick = () => {
    if (hasChildren && !collapsed) {
      setIsExpanded(!isExpanded);
    } else {
      onNavigate?.(item);
      item?.onClick?.();
    }
  };

  const buttonContent = (
    <GlassButton
      variant="ghost"
      onClick={handleClick}
      disabled={item?.disabled}
      className={cn(
        `w-full flex items-center glass-gap-3 glass-radius-md transition-all duration-[${ANIMATION.DURATION.fast}ms] relative group`,
        "hover:bg-white/10 focus:outline-none",
        collapsed
          ? "glass-px-2 glass-py-2.5 justify-center"
          : "glass-px-3 glass-py-2",
        "glass-text-sm font-medium",
        isActive
          ? 'bg-primary/15 text-primary shadow-[0_0_0_2px_${glassStyles.borderColor || "hsl(var(--glass-color-primary)/0.2)"}]'
          : "",
        !isActive && "glass-text-secondary hover:text-foreground",
        item?.disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {/* Icon */}
      {item?.icon && (
        <span className="glass-flex-shrink-0 glass-w-6 glass-h-6 glass-flex glass-items-center glass-justify-center glass-text-lg">
          {React.isValidElement(item.icon)
            ? React.cloneElement(item.icon as React.ReactElement<any>, {
                className: cn(
                  (item.icon as React.ReactElement<any>).props?.className,
                  collapsed ? "w-6 h-6" : "w-5 h-5"
                ),
              })
            : item.icon}
        </span>
      )}

      {/* Label and badge */}
      {!collapsed && (
        <>
          <span className="glass-flex-1 glass-text-left glass-whitespace-nowrap glass-truncate glass-leading-5">
            {item?.label}
          </span>

          {item?.badge && (
            <span
              className={cn(
                `flex-shrink-0 glass-px-2 glass-py-1 glass-text-xs glass-radius-full font-medium transition-all duration-[${ANIMATION.DURATION.fast}ms]`,
                "bg-white/8 border border-white/15 shadow-md glass-backdrop-blur-md",
                "group-hover:bg-white/12 group-hover:border-white/20 group-hover:shadow-lg",
                "relative z-10" // Ensure badge stays above any background effects
              )}
              style={{
                color: "var(--glass-white)",
              }}
            >
              {item?.badge}
            </span>
          )}

          {hasChildren && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn(
                `flex-shrink-0 transition-transform duration-[${ANIMATION.DURATION.fast}ms] glass-text-primary/50`,
                isExpanded ? "rotate-90" : "rotate-0"
              )}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          )}
        </>
      )}
    </GlassButton>
  );

  return (
    <li>
      {collapsed && item?.label ? (
        <GlassTooltip
          content={item?.label}
          placement="right"
          zIndex={9999}
          showDelay={300}
        >
          {buttonContent}
        </GlassTooltip>
      ) : (
        buttonContent
      )}

      {/* Children */}
      {hasChildren && !collapsed && isExpanded && (
        <Motion preset="slideDown" className="glass-mt-1">
          <div className="glass-ml-2 glass-pl-4 glass-border-l glass-border-glass-border/20">
            <SidebarNavigation items={item?.children!} level={level + 1} />
          </div>
        </Motion>
      )}
    </li>
  );
}

/**
 * SidebarBrand component
 */
export interface SidebarBrandProps {
  logo?: React.ReactNode;
  title?: string;
  subtitle?: string;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export function SidebarBrand({
  logo,
  title,
  subtitle,
  href,
  onClick,
  className,
}: SidebarBrandProps) {
  const { collapsed } = useSidebarContext();

  const Content = () => (
    <div
      className={cn(
        "flex items-center glass-gap-3",
        collapsed && "justify-center"
      )}
    >
      {logo && (
        <div className="glass-flex-shrink-0 glass-w-8 glass-h-8 glass-flex glass-items-center glass-justify-center">
          {logo}
        </div>
      )}

      {!collapsed && (title || subtitle) && (
        <div className="glass-flex-1 glass-min-w-0">
          {title && (
            <h1 className="glass-text-lg glass-font-bold glass-text-primary glass-truncate">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="glass-text-xs glass-text-secondary glass-truncate">
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  );

  if (href) {
    return (
      <a href={href} className={cn("block", className)}>
        <Content />
      </a>
    );
  }

  if (onClick) {
    return (
      <GlassButton
        variant="ghost"
        onClick={onClick}
        className={cn("block w-full text-left", className)}
      >
        <Content />
      </GlassButton>
    );
  }

  return (
    <div className={className}>
      <Content />
    </div>
  );
}

/**
 * SidebarUserInfo component
 */
export interface SidebarUserInfoProps {
  name: string;
  email?: string;
  avatar?: string;
  status?: "online" | "away" | "busy" | "offline";
  actions?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export function SidebarUserInfo({
  name,
  email,
  avatar,
  status,
  actions,
  onClick,
  className,
}: SidebarUserInfoProps) {
  const { collapsed } = useSidebarContext();

  if (collapsed) {
    return (
      <div className={cn("flex justify-center", className)}>
        <div className="glass-relative">
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              className="glass-w-8 glass-h-8 glass-radius-full glass-object-cover"
            />
          ) : (
            <div className="glass-w-8 glass-h-8 glass-radius-full glass-surface-primary/20 glass-flex glass-items-center glass-justify-center">
              <span className="glass-text-sm glass-font-medium">
                {name.charAt(0)}
              </span>
            </div>
          )}

          {status && (
            <div
              className={cn(
                "absolute -bottom-0.5 -right-0.5 w-3 h-3 glass-radius-full border border-background",
                {
                  "bg-green-500": status === "online",
                  "bg-yellow-500": status === "away",
                  "bg-red-500": status === "busy",
                  "bg-gray-500": status === "offline",
                }
              )}
            />
          )}
        </div>
      </div>
    );
  }

  const Content = () => (
    <div className="glass-flex glass-items-center glass-gap-3">
      <div className="glass-relative glass-flex-shrink-0">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            className="glass-w-10 glass-h-10 glass-radius-full glass-object-cover"
          />
        ) : (
          <div className="glass-w-10 glass-h-10 glass-radius-full glass-surface-primary/20 glass-flex glass-items-center glass-justify-center">
            <span className="glass-text-lg glass-font-medium">
              {name.charAt(0)}
            </span>
          </div>
        )}

        {status && (
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 w-3 h-3 glass-radius-full border-2 border-background",
              {
                "bg-green-500": status === "online",
                "bg-yellow-500": status === "away",
                "bg-red-500": status === "busy",
                "bg-gray-500": status === "offline",
              }
            )}
          />
        )}
      </div>

      <div className="glass-flex-1 glass-min-w-0">
        <p className="glass-text-sm glass-font-medium glass-text-primary glass-truncate">
          {name}
        </p>
        {email && (
          <p className="glass-text-xs glass-text-secondary glass-truncate">
            {email}
          </p>
        )}
      </div>

      {actions && <div className="glass-flex-shrink-0">{actions}</div>}
    </div>
  );

  if (onClick) {
    return (
      <GlassButton
        variant="ghost"
        onClick={onClick}
        className={cn(
          "w-full text-left glass-p-2 glass-radius-md",
          "hover:bg-muted/50 transition-colors",
          className
        )}
      >
        <Content />
      </GlassButton>
    );
  }

  return (
    <div className={className}>
      <Content />
    </div>
  );
}
